import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';

const API_BASE = 'https://faredrift.com/api';

// True when running inside the iOS/Android app. Per Apple guideline 3.1.1 /
// 3.1.3(b), paid content accessed in the app must also be purchasable via
// Apple In-App Purchase — so the native app sells Premium through IAP
// (RevenueCat), while the website continues to sell via Stripe.
const IS_NATIVE_APP = Capacitor.isNativePlatform();

const ONESIGNAL_APP_ID = '399a7602-2a58-43b8-b954-4006dd45b810';

// RevenueCat public SDK key (safe in client code). In the native app, Premium
// is sold via Apple In-App Purchase through RevenueCat; access is granted by
// the "premium" entitlement. The website keeps selling via Stripe.
const RC_APPLE_KEY = 'appl_ZwJqjHZTTUAHrpyXWxcxeGzrNMt';
const RC_ENTITLEMENT = 'premium';
const PRODUCT_MONTHLY = 'com.faredrift.app.premium.monthly';
const PRODUCT_YEARLY = 'com.faredrift.app.premium.yearly';
// Photos always load from the live site, so the iOS/Android apps (which bundle
// their own copy of the code) see the same images as the website.
const IMG_BASE = 'https://faredrift.com';

// DESTINATIONS DATA (fallback if API fails)
const DESTINATIONS = {
  europe: { name: 'Europe', emoji: '🏰', destinations: [
    { code: 'CDG', city: 'Paris', country: 'France', emoji: '🇫🇷', icon: '🎨', dream: 'sketching by the Seine at golden hour', color: '#E8B4BC' },
    { code: 'FCO', city: 'Rome', country: 'Italy', emoji: '🇮🇹', icon: '☕', dream: 'sipping espresso in a sun-drenched piazza', color: '#D4A574' },
    { code: 'BCN', city: 'Barcelona', country: 'Spain', emoji: '🇪🇸', icon: '🌊', dream: 'watching sunset from a rooftop tapas bar', color: '#F4A460' },
    { code: 'LHR', city: 'London', country: 'UK', emoji: '🇬🇧', icon: '🫖', dream: 'having afternoon tea while rain patters the windows', color: '#A0522D' },
    { code: 'AMS', city: 'Amsterdam', country: 'Netherlands', emoji: '🇳🇱', icon: '🚲', dream: 'cycling along canals lined with tulips', color: '#FF6B35' },
    { code: 'LIS', city: 'Lisbon', country: 'Portugal', emoji: '🇵🇹', icon: '🎸', dream: 'listening to fado in a candlelit tavern', color: '#E6B89C' },
    { code: 'ATH', city: 'Athens', country: 'Greece', emoji: '🇬🇷', icon: '🏛️', dream: 'watching sunrise paint the Acropolis gold', color: '#87CEEB' },
    { code: 'PRG', city: 'Prague', country: 'Czech Republic', emoji: '🇨🇿', icon: '🌉', dream: 'wandering cobblestone streets at twilight', color: '#DEB887' },
    { code: 'VIE', city: 'Vienna', country: 'Austria', emoji: '🇦🇹', icon: '🎻', dream: 'attending a concert in a gilded palace', color: '#FFD700' },
    { code: 'DUB', city: 'Dublin', country: 'Ireland', emoji: '🇮🇪', icon: '🍀', dream: 'sharing stories with locals in a cozy pub', color: '#228B22' },
    { code: 'KEF', city: 'Reykjavik', country: 'Iceland', emoji: '🇮🇸', icon: '🌌', dream: 'soaking in hot springs under Northern Lights', color: '#4B0082' },
    { code: 'VCE', city: 'Venice', country: 'Italy', emoji: '🇮🇹', icon: '🛶', dream: 'gliding through canals as church bells echo', color: '#40E0D0' },
  ]},
  asia: { name: 'Asia', emoji: '🏯', destinations: [
    { code: 'NRT', city: 'Tokyo', country: 'Japan', emoji: '🇯🇵', icon: '🌸', dream: 'watching cherry blossoms in a temple garden', color: '#FFB7C5' },
    { code: 'KIX', city: 'Osaka', country: 'Japan', emoji: '🇯🇵', icon: '🍜', dream: 'slurping ramen in a bustling food alley', color: '#FF6B6B' },
    { code: 'PEK', city: 'Beijing', country: 'China', emoji: '🇨🇳', icon: '🏯', dream: 'walking the Great Wall as mist rises', color: '#CD5C5C' },
    { code: 'PVG', city: 'Shanghai', country: 'China', emoji: '🇨🇳', icon: '🌃', dream: 'watching neon lights on the Bund at night', color: '#9370DB' },
    { code: 'TPE', city: 'Taipei', country: 'Taiwan', emoji: '🇹🇼', icon: '🏮', dream: 'exploring night markets full of flavors', color: '#FF4500' },
    { code: 'HKG', city: 'Hong Kong', country: 'Hong Kong', emoji: '🇭🇰', icon: '🌆', dream: 'riding Star Ferry as skyscrapers sparkle', color: '#4169E1' },
    { code: 'ICN', city: 'Seoul', country: 'South Korea', emoji: '🇰🇷', icon: '🎤', dream: 'discovering K-culture in Gangnam streets', color: '#FF69B4' },
    { code: 'BKK', city: 'Bangkok', country: 'Thailand', emoji: '🇹🇭', icon: '🛕', dream: 'finding peace in golden temples', color: '#FFD700' },
    { code: 'SIN', city: 'Singapore', country: 'Singapore', emoji: '🇸🇬', icon: '🌳', dream: 'wandering futuristic gardens at night', color: '#00CED1' },
    { code: 'HAN', city: 'Hanoi', country: 'Vietnam', emoji: '🇻🇳', icon: '🛵', dream: 'sipping Vietnamese coffee watching life flow', color: '#8B4513' },
  ]},
  pacific: { name: 'Pacific Islands', emoji: '🏝️', destinations: [
    { code: 'HNL', city: 'Honolulu', country: 'Hawaii', emoji: '🌺', icon: '🏄', dream: 'catching waves at sunrise over Diamond Head', color: '#FF6B6B' },
    { code: 'OGG', city: 'Maui', country: 'Hawaii', emoji: '🌺', icon: '🌅', dream: 'watching sunrise atop Haleakalā volcano', color: '#FF8C00' },
    { code: 'PPT', city: 'Tahiti', country: 'French Polynesia', emoji: '🇵🇫', icon: '🌊', dream: 'swimming in crystal lagoons', color: '#00CED1' },
    { code: 'NAN', city: 'Fiji', country: 'Fiji', emoji: '🇫🇯', icon: '🐚', dream: 'feeling white sand between your toes', color: '#20B2AA' },
    { code: 'RAR', city: 'Rarotonga', country: 'Cook Islands', emoji: '🇨🇰', icon: '🥥', dream: 'sipping coconut water in paradise', color: '#3CB371' },
    { code: 'GUM', city: 'Guam', country: 'Guam', emoji: '🇬🇺', icon: '🐠', dream: 'snorkeling through vibrant coral reefs', color: '#1E90FF' },
    { code: 'AKL', city: 'Auckland', country: 'New Zealand', emoji: '🇳🇿', icon: '⛵', dream: 'sailing the City of Sails at golden hour', color: '#4682B4' },
  ]},
  caribbean: { name: 'Caribbean', emoji: '🌴', destinations: [
    { code: 'SJU', city: 'San Juan', country: 'Puerto Rico', emoji: '🇵🇷', icon: '🏰', dream: 'wandering colorful Old San Juan streets', color: '#FF6347' },
    { code: 'CUN', city: 'Cancún', country: 'Mexico', emoji: '🇲🇽', icon: '🏖️', dream: 'lounging on white sand with turquoise waves', color: '#00CED1' },
    { code: 'MBJ', city: 'Montego Bay', country: 'Jamaica', emoji: '🇯🇲', icon: '🎶', dream: 'feeling reggae rhythms on a beach night', color: '#FFD700' },
    { code: 'PUJ', city: 'Punta Cana', country: 'Dominican Rep.', emoji: '🇩🇴', icon: '🌅', dream: 'waking to palm trees over paradise', color: '#FF8C00' },
    { code: 'NAS', city: 'Nassau', country: 'Bahamas', emoji: '🇧🇸', icon: '🐷', dream: 'swimming with pigs in crystal waters', color: '#FF69B4' },
    { code: 'AUA', city: 'Aruba', country: 'Aruba', emoji: '🇦🇼', icon: '🌵', dream: 'discovering hidden coves and divi-divi trees', color: '#F4A460' },
    { code: 'BGI', city: 'Barbados', country: 'Barbados', emoji: '🇧🇧', icon: '🍹', dream: 'sipping rum punch as the sun sets', color: '#DC143C' },
    { code: 'UVF', city: 'St. Lucia', country: 'St. Lucia', emoji: '🇱🇨', icon: '⛰️', dream: 'gazing at Pitons rising from the sea', color: '#228B22' },
  ]}
};

const ALL_DESTINATIONS = Object.values(DESTINATIONS).flatMap(r => r.destinations.map(d => ({ ...d, region: r.name })));

// Sample fares shown only when the live API is unreachable
const generatePrices = (destinations) => {
  const today = new Date();
  return destinations.map((dest, idx) => {
    const regionBase = { 'Europe': 550, 'Asia': 850, 'Pacific Islands': 650, 'Caribbean': 400 };
    const base = (regionBase[dest.region] || 500) + (idx * 5);
    const weeks = 3 + (idx % 5) * 2;
    const dur = [7, 10, 14][idx % 3];
    const dep = new Date(today); dep.setDate(dep.getDate() + weeks * 7);
    const ret = new Date(dep); ret.setDate(ret.getDate() + dur);
    const price = Math.max(299, base + Math.floor(Math.random() * 200) - 50);
    const airlines = { 'Europe': ['United','Lufthansa','Delta'], 'Asia': ['ANA','JAL','United'], 'Pacific Islands': ['Hawaiian','United'], 'Caribbean': ['JetBlue','American','Delta'] };
    return {
      id: `${dest.code}-0`,
      destination: dest,
      code: dest.code,
      city: dest.city,
      country: dest.country,
      region: dest.region,
      emoji: dest.emoji,
      icon: dest.icon,
      dream: dest.dream,
      color: dest.color,
      departDate: dep,
      returnDate: ret,
      duration: dur,
      price,
      airline: airlines[dest.region]?.[idx % 3] || 'United',
      stops: [0, 1, 2][Math.floor(Math.random() * 3)]
    };
  });
};

const num = (p) => {
  const n = typeof p === 'number' ? p : parseFloat(p);
  return isNaN(n) ? null : n;
};

const fmtPrice = (p) => {
  const n = num(p);
  return n === null ? '—' : `$${Math.round(n).toLocaleString()}`;
};

const fmt = (d) => {
  if (!d) return '—';
  if (typeof d === 'string') d = new Date(d + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const fmtFull = (d) => {
  if (!d) return '—';
  if (typeof d === 'string') d = new Date(d + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const fmtMonth = (d) => {
  if (!d) return '—';
  if (typeof d === 'string') d = new Date(d + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const stopsText = (stops) => {
  if (stops === null || stops === undefined) return '—';
  if (stops === 0) return 'Nonstop';
  if (stops === 1) return '1 stop';
  return `${stops} stops`;
};

const sentence = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) + '.' : '');

const parseDeal = (d, localDestMap, idSuffix = '0') => {
  const local = localDestMap[d.code] || {};
  return {
    id: `${d.code}-${idSuffix}`,
    destination: d,
    code: d.code,
    city: d.city,
    country: d.country,
    region: d.region,
    emoji: local.emoji || d.emoji,
    icon: local.icon || d.icon,
    dream: local.dream || d.dream,
    color: local.color || d.color,
    departDate: d.departDate || null,
    returnDate: d.returnDate || null,
    duration: d.duration,
    price: num(d.price),
    wasPrice: num(d.wasPrice),
    airline: d.airline,
    stops: d.stops
  };
};

// FareDrift "Crest" roundel — ink + brass gold, FD monogram in Cormorant
// Garamond. Drawn inline so the real webfont renders it (the brand SVG files
// fall back to Times when used as plain images — see brand/README.md).
const LogoMark = ({ size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 256 256" aria-hidden="true" style={{ flexShrink: 0 }}>
    <circle cx="128" cy="128" r="116" fill="#231F18" stroke="#B0862F" strokeWidth="8" />
    <text x="128" y="128" fill="#B0862F" fontSize="132" textAnchor="middle" dominantBaseline="central" letterSpacing="1" fontFamily="'Cormorant Garamond', Georgia, serif" fontWeight="700">FD</text>
  </svg>
);

// Warm duotone gradients per region for card art (auto-replaced when a real
// photo exists at /images/deals/<CODE>.jpg)
const REGION_ART = {
  'Europe': 'linear-gradient(160deg, #D9C6A8 0%, #B08E5F 60%, #8A6B45 100%)',
  'Asia': 'linear-gradient(160deg, #DBBCA8 0%, #B07B63 60%, #7E5343 100%)',
  'Pacific Islands': 'linear-gradient(160deg, #BCD1C6 0%, #7FA398 60%, #5A7A70 100%)',
  'Caribbean': 'linear-gradient(160deg, #E3CDA4 0%, #C09A5E 60%, #8F6E3E 100%)',
};

// Image with graceful fallback: shows the styled fallback until/unless the
// photo at `src` loads (so real photography can be dropped in later).
const Photo = ({ src, alt, fallback }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      {!loaded && fallback}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: loaded ? 'block' : 'none' }}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    </div>
  );
};

const LayerImg = ({ src }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      src={src}
      alt=""
      onLoad={() => setLoaded(true)}
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: loaded ? 'block' : 'none' }}
    />
  );
};

// Hero art: today's deal city photo if it exists, else the general hero
// photo, else the built-in illustration. Layers stack; best one paints on top.
const HeroPhoto = ({ code }) => (
  <div style={{ position: 'absolute', inset: 0 }}>
    <FjordScene />
    <LayerImg src={`${IMG_BASE}/images/hero.jpg`} />
    {code ? <LayerImg src={`${IMG_BASE}/images/deals/${code}.jpg`} /> : null}
  </div>
);

// Illustrated fjord scene — hero fallback until a photo lands at /images/hero.jpg
const FjordScene = () => (
  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #E8C49C 0%, #D99C6B 30%, #A96F4F 48%, #5C6E72 58%, #39505A 74%, #23343C 100%)' }}>
    <div style={{ position: 'absolute', top: '26%', left: '68%', width: 74, height: 74, borderRadius: '50%', background: 'radial-gradient(circle, #FBEAD1 0%, #F2CE9E 55%, rgba(242,206,158,0) 72%)', opacity: 0.9 }} />
    <svg viewBox="0 0 1400 560" preserveAspectRatio="xMidYMax slice" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      <polygon points="0,330 180,180 340,300 470,210 640,330 1400,330 1400,560 0,560" fill="#4A5C60" opacity="0.85" />
      <polygon points="520,330 760,150 980,310 1120,230 1290,330 1400,300 1400,560 520,560" fill="#3A4B50" opacity="0.9" />
      <polygon points="0,360 240,260 520,368 830,282 1130,372 1400,320 1400,560 0,560" fill="#2C3C42" />
      <rect y="392" width="1400" height="168" fill="#20303A" opacity="0.55" />
      <polygon points="0,392 1400,392 1400,560 0,560" fill="url(#water)" opacity="0.5" />
      <defs>
        <linearGradient id="water" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7B8E8C" />
          <stop offset="100%" stopColor="#1B2A32" />
        </linearGradient>
      </defs>
    </svg>
  </div>
);

export default function FareDrift() {
  const [isPremium, setIsPremium] = useState(false);
  const [userEmail, setUserEmail] = useState(localStorage.getItem('fd_email') || '');
  const [region, setRegion] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [showWishlist, setShowWishlist] = useState(false);
  const [adventure, setAdventure] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeEmail, setUpgradeEmail] = useState('');
  const [upgradeError, setUpgradeError] = useState('');
  const [upgradePlan, setUpgradePlan] = useState('monthly');
  const [apiDeals, setApiDeals] = useState(null);
  const [dealOfDay, setDealOfDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailSignup, setEmailSignup] = useState('');
  const [emailSignupSuccess, setEmailSignupSuccess] = useState(false);
  const [emailSignupError, setEmailSignupError] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [welcomeBanner, setWelcomeBanner] = useState(false);
  const [rcPrices, setRcPrices] = useState(null); // { monthly, yearly } localized price strings
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteDone, setDeleteDone] = useState(false);
  const rcRef = useRef(null); // configured RevenueCat Purchases instance (native only)

  // Returns a configured RevenueCat Purchases instance, configuring it on the
  // spot if the startup effect hasn't finished yet (so a fast tap still works).
  const getPurchases = async () => {
    if (rcRef.current) return rcRef.current;
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    try { await Purchases.configure({ apiKey: RC_APPLE_KEY }); } catch (e) { /* already configured */ }
    rcRef.current = Purchases;
    return Purchases;
  };

  // Fetch the two subscription products from the store and cache their live
  // prices + product objects in state. Returns the product objects by id.
  const loadRcProducts = async (Purchases, cancelled) => {
    const { products } = await Purchases.getProducts({
      productIdentifiers: [PRODUCT_MONTHLY, PRODUCT_YEARLY],
    });
    const byId = {};
    (products || []).forEach(p => { byId[p.identifier] = p; });
    if (!cancelled && products?.length) {
      setRcPrices({
        monthly: byId[PRODUCT_MONTHLY]?.priceString || '$4.99',
        yearly: byId[PRODUCT_YEARLY]?.priceString || '$49',
        monthlyProduct: byId[PRODUCT_MONTHLY],
        yearlyProduct: byId[PRODUCT_YEARLY],
      });
    }
    return byId;
  };

  // Fetch destinations from API
  useEffect(() => {
    const localDestMap = {};
    ALL_DESTINATIONS.forEach(d => { localDestMap[d.code] = d; });

    const fetchData = async () => {
      const [destResult, dodResult] = await Promise.allSettled([
        fetch(`${API_BASE}/destinations`).then(r => r.json()),
        fetch(`${API_BASE}/deal-of-day`).then(r => r.json()),
      ]);

      if (destResult.status === 'fulfilled' && destResult.value.success && destResult.value.data) {
        setApiDeals(destResult.value.data.map(d => parseDeal(d, localDestMap)));
      } else {
        console.log('API fetch failed, using sample data');
      }
      if (dodResult.status === 'fulfilled' && dodResult.value.success && dodResult.value.data) {
        setDealOfDay(parseDeal(dodResult.value.data, localDestMap, 'dod'));
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  // Check premium status
  useEffect(() => {
    const checkPremium = async () => {
      const email = localStorage.getItem('fd_email');
      if (email) {
        try {
          const res = await fetch(`${API_BASE}/payments/status?email=${encodeURIComponent(email)}`);
          const data = await res.json();
          if (data.success && data.isPremium) {
            setIsPremium(true);
          }
        } catch (err) {
          console.log('Could not check premium status');
        }
      }
    };
    checkPremium();
  }, []);

  // Restore saved favorites from the server once we know the member's email
  // and the deals have loaded. Failures are silent — favorites are a
  // convenience and must never break the page.
  useEffect(() => {
    if (!userEmail || !apiDeals) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/favorites?email=${encodeURIComponent(userEmail)}`);
        const data = await res.json();
        if (data.success && Array.isArray(data.codes) && data.codes.length) {
          setWishlist(prev => {
            const have = new Set(prev.map(d => d.code));
            const restored = data.codes
              .filter(c => !have.has(c))
              .map(c => apiDeals.find(d => d.code === c))
              .filter(Boolean);
            return restored.length ? [...prev, ...restored] : prev;
          });
        }
      } catch (e) { /* silent — see note above */ }
    })();
  }, [userEmail, apiDeals]);

  // Check for successful payment return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_id')) {
      setIsPremium(true);
      setWelcomeBanner(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // In-app purchases (native app only) — RevenueCat.
  // Initializes the SDK, reads current entitlement, and loads live localized
  // prices for the two subscription products.
  useEffect(() => {
    if (!IS_NATIVE_APP) return;
    let cancelled = false;
    (async () => {
      try {
        const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');
        try { await Purchases.setLogLevel({ level: LOG_LEVEL.ERROR }); } catch (e) { /* non-fatal */ }
        await Purchases.configure({ apiKey: RC_APPLE_KEY });
        rcRef.current = Purchases; // mark configured for the purchase/restore handlers

        // Restore premium state from the entitlement (survives reinstalls)
        try {
          const { customerInfo } = await Purchases.getCustomerInfo();
          if (!cancelled && customerInfo?.entitlements?.active?.[RC_ENTITLEMENT]) {
            setIsPremium(true);
          }
        } catch (e) { /* entitlement check is best effort */ }

        // Best-effort preload of live localized prices. If this fails or is
        // slow, the purchase handler re-fetches on demand, so the button
        // always works.
        try { await loadRcProducts(Purchases, cancelled); } catch (e) { /* handled on tap */ }
      } catch (e) {
        console.log('RevenueCat init failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Push notifications (native app only) — OneSignal
  useEffect(() => {
    if (!IS_NATIVE_APP) return;
    import('@onesignal/capacitor-plugin').then(({ default: OneSignal }) => {
      OneSignal.initialize(ONESIGNAL_APP_ID);
      OneSignal.Notifications.requestPermission(false).then((accepted) => {
        console.log('Push permission accepted:', accepted);
      });
    }).catch((e) => console.log('OneSignal init failed', e));
  }, []);

  // Tag the device with premium status so deal alerts can be segmented
  // (premium gets everything daily, free gets the weekly-grade pushes)
  useEffect(() => {
    if (!IS_NATIVE_APP) return;
    import('@onesignal/capacitor-plugin').then(({ default: OneSignal }) => {
      OneSignal.User.addTag('premium', isPremium ? 'true' : 'false');
    }).catch(() => {});
  }, [isPremium]);

  const usingSampleData = !loading && apiDeals === null;

  // Use API data or fallback - only show destinations with prices
  const deals = useMemo(() => {
    let data = apiDeals || generatePrices(ALL_DESTINATIONS);
    data = data.filter(d => num(d.price) > 0);
    if (region !== 'all') {
      data = data.filter(d => (d.region || d.destination?.region) === region);
    }
    return [...data].sort((a, b) => (num(a.price) || 99999) - (num(b.price) || 99999));
  }, [apiDeals, region]);

  const currentDealOfDay = dealOfDay || (deals.length > 0 ? deals[0] : null);

  const toggleWish = (deal) => {
    const adding = !wishlist.some(d => d.id === deal.id);
    setWishlist(prev => adding ? [...prev, deal] : prev.filter(d => d.id !== deal.id));
    // If we know who this is, persist the heart on the server too — favorites
    // then survive refreshes and follow the member across devices.
    const email = localStorage.getItem('fd_email');
    if (email && deal.code) {
      // No Content-Type header on purpose: keeps this a CORS "simple request"
      // (no OPTIONS preflight), which the iOS WebView requires with our server.
      fetch(`${API_BASE}/favorites`, {
        method: 'POST',
        body: JSON.stringify({ email, code: deal.code, action: adding ? 'add' : 'remove' }),
      }).catch(() => {});
    }
  };
  const inWish = (deal) => wishlist.some(d => d.id === deal.id);
  const spin = () => {
    if (!deals.length || spinning) return;
    setSpinning(true);
    let i = 0;
    const int = setInterval(() => {
      setAdventure(deals[Math.floor(Math.random() * deals.length)]);
      if (++i >= 20) { clearInterval(int); setSpinning(false); }
    }, 100);
  };

  // Native (Apple IAP via RevenueCat): buy the selected subscription.
  const handleNativePurchase = async () => {
    setUpgradeError('');
    setUpgradeLoading(true);
    try {
      const Purchases = await getPurchases();
      const targetId = upgradePlan === 'yearly' ? PRODUCT_YEARLY : PRODUCT_MONTHLY;

      // Use the preloaded product if available; otherwise fetch on demand now
      // (this is the key fix — the button always attempts a real purchase even
      // if the startup preload failed or was slow, e.g. in App Review).
      let product = upgradePlan === 'yearly' ? rcPrices?.yearlyProduct : rcPrices?.monthlyProduct;
      if (!product) {
        const byId = await loadRcProducts(Purchases, false);
        product = byId[targetId];
      }
      if (!product) {
        setUpgradeError('The App Store isn’t responding right now. Please check your connection and try again in a moment.');
        setUpgradeLoading(false);
        return;
      }

      const { customerInfo } = await Purchases.purchaseStoreProduct({ product });
      if (customerInfo?.entitlements?.active?.[RC_ENTITLEMENT]) {
        setIsPremium(true);
        setShowUpgrade(false);
        setWelcomeBanner(true);
      }
    } catch (e) {
      // User cancelling the Apple sheet is not an error worth showing
      if (!e?.userCancelled && e?.code !== 'PURCHASE_CANCELLED' && !/cancel/i.test(e?.message || '')) {
        setUpgradeError(e?.message || 'Purchase could not be completed. Please try again.');
      }
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Native: restore a previous purchase (Apple requires this option).
  const handleRestore = async () => {
    setUpgradeError('');
    setRestoreLoading(true);
    try {
      const Purchases = await getPurchases();
      const { customerInfo } = await Purchases.restorePurchases();
      if (customerInfo?.entitlements?.active?.[RC_ENTITLEMENT]) {
        setIsPremium(true);
        setShowUpgrade(false);
        setWelcomeBanner(true);
      } else {
        setUpgradeError('No active subscription found to restore.');
      }
    } catch (e) {
      setUpgradeError('Could not restore purchases. Please try again.');
    } finally {
      setRestoreLoading(false);
    }
  };

  // Sign out: forget who this is on this device. Server data stays.
  const handleSignOut = () => {
    localStorage.removeItem('fd_email');
    setUserEmail('');
    setIsPremium(false);
    setWishlist([]);
    setShowAccount(false);
    setDeleteConfirm(false);
    setDeleteError('');
  };

  // Delete account: permanently removes the member's data on the server
  // (subscriber record, favorites, account row), then signs out locally.
  // Required by App Store Guideline 5.1.1(v).
  const handleDeleteAccount = async () => {
    setDeleteError('');
    setDeleteLoading(true);
    try {
      // No Content-Type header on purpose — avoids the CORS preflight that the
      // iOS WebView blocks against our server (see favorites POST).
      const res = await fetch(`${API_BASE}/account/delete`, {
        method: 'POST',
        body: JSON.stringify({ email: userEmail }),
      });
      const data = await res.json();
      if (data.success) {
        localStorage.removeItem('fd_email');
        setUserEmail('');
        setIsPremium(false);
        setWishlist([]);
        setDeleteConfirm(false);
        setShowAccount(false);
        setDeleteDone(true);
      } else {
        setDeleteError(data.error || 'Could not delete your account. Please try again.');
      }
    } catch (e) {
      setDeleteError('Could not reach the server. Please check your connection and try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Web (Stripe checkout) OR native (Apple IAP) depending on platform.
  const handleUpgrade = async () => {
    if (IS_NATIVE_APP) {
      return handleNativePurchase();
    }
    if (!upgradeEmail || !upgradeEmail.includes('@')) {
      setUpgradeError('Please enter a valid email address.');
      return;
    }
    setUpgradeError('');
    setUpgradeLoading(true);

    try {
      const res = await fetch(`${API_BASE}/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: upgradeEmail, plan: upgradePlan })
      });
      const data = await res.json();

      if (data.success && data.checkoutUrl) {
        localStorage.setItem('fd_email', upgradeEmail);
        window.location.href = data.checkoutUrl;
      } else {
        setUpgradeError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setUpgradeError('Could not connect to the payment system. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Handle email signup for deal alerts
  const handleEmailSignup = async () => {
    if (!emailSignup || !emailSignup.includes('@')) {
      setEmailSignupError('Please enter a valid email address.');
      return;
    }
    setEmailSignupError('');
    try {
      // No Content-Type header on purpose — avoids the CORS preflight that the
      // iOS WebView blocks against our server (see favorites POST).
      const res = await fetch(`${API_BASE}/subscribe`, {
        method: 'POST',
        body: JSON.stringify({ email: emailSignup, source: 'deal_alerts_free' })
      });
      const data = await res.json();
      if (data.success) {
        setEmailSignupSuccess(true);
        localStorage.setItem('fd_email', emailSignup);
        setUserEmail(emailSignup);
      } else {
        setEmailSignupError(data.error || 'Could not subscribe. Please try again.');
      }
    } catch (err) {
      setEmailSignupError('Could not subscribe. Please try again.');
    }
  };

  const handleLogin = async () => {
    if (!loginEmail || !loginEmail.includes('@')) {
      setLoginError('Please enter your email address.');
      return;
    }
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${API_BASE}/payments/status?email=${encodeURIComponent(loginEmail)}`);
      const data = await res.json();
      if (data.success && data.isPremium) {
        localStorage.setItem('fd_email', loginEmail);
        setIsPremium(true);
        setUserEmail(loginEmail);
        setShowLogin(false);
      } else {
        setLoginError('No premium subscription found for this email.');
      }
    } catch (err) {
      setLoginError('Could not verify subscription. Please try again.');
    }
    setLoginLoading(false);
  };

  const openUpgrade = () => setShowUpgrade(true);

  const filterAndScroll = (r) => {
    setRegion(r);
    document.getElementById('deals')?.scrollIntoView({ behavior: 'smooth' });
  };

  const regionTabs = [
    { id: 'all', name: 'All' },
    { id: 'Europe', name: 'Europe' },
    { id: 'Asia', name: 'Asia' },
    { id: 'Pacific Islands', name: 'Pacific' },
    { id: 'Caribbean', name: 'Caribbean' },
  ];

  const heroCity = currentDealOfDay?.city;
  const heroPlace = currentDealOfDay?.code === 'OSL' ? 'the fjords' : heroCity;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F7F1E8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Instrument Sans', sans-serif", color: '#231F18' }}>
        <style>{`@keyframes fdPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
        <div style={{ textAlign: 'center', animation: 'fdPulse 1.4s ease infinite' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, justifyContent: 'center' }}>
            <LogoMark size={34} />
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, letterSpacing: '-0.01em' }}>FareDrift</span>
          </div>
          <div style={{ marginTop: 14, fontSize: 15, color: '#6B6459' }}>Watching every fare out of Denver…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F7F1E8', fontFamily: "'Instrument Sans', -apple-system, sans-serif", color: '#231F18' }}>
      <style>{`
        * { box-sizing: border-box; }
        html { -webkit-font-smoothing: antialiased; scroll-behavior: smooth; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

        .serif { font-family: 'Playfair Display', Georgia, serif; }
        .eyebrow { font-size: 13px; letter-spacing: 0.2em; text-transform: uppercase; color: #C8613A; font-weight: 600; }

        .fd-nav { position: sticky; top: 0; z-index: 200; background: rgba(247,241,232,0.92); backdrop-filter: blur(12px); border-bottom: 1px solid #EAE1D2; padding-top: env(safe-area-inset-top, 0px); }
        .fd-nav-inner { max-width: 1440px; margin: 0 auto; padding: 16px 44px; display: flex; align-items: center; justify-content: space-between; gap: 14px; }
        .fd-nav-links { display: flex; align-items: center; gap: 30px; font-size: 15px; font-weight: 500; color: #4A4437; }
        .fd-nav-link { background: none; border: none; font: inherit; color: inherit; cursor: pointer; padding: 0; white-space: nowrap; }
        .fd-nav-link:hover { color: #231F18; }
        .fd-signin { border-bottom: 1px solid #231F18; padding-bottom: 2px; }

        .btn { border: none; border-radius: 999px; font-family: inherit; font-weight: 600; cursor: pointer; transition: all 0.15s ease; white-space: nowrap; }
        .btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-ink { background: #231F18; color: #F7F1E8; padding: 10px 20px; font-size: 15px; }
        .btn-ink:hover:not(:disabled) { background: #3A342A; }
        .btn-terra { background: #C8613A; color: #fff; padding: 12px 24px; font-size: 15px; }
        .btn-terra:hover:not(:disabled) { background: #B55532; }
        .btn-outline { background: transparent; color: #4A4437; padding: 9px 18px; font-size: 14px; border: 1px solid #D6CDBB; }
        .btn-outline:hover { border-color: #A79E8D; color: #231F18; }

        .chip { padding: 9px 18px; border-radius: 999px; border: 1px solid #D6CDBB; background: transparent; color: #4A4437; font-family: inherit; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.15s ease; }
        .chip:hover { border-color: #A79E8D; color: #231F18; }
        .chip.on { background: #231F18; border-color: #231F18; color: #F7F1E8; }

        .dealcard { background: #FFFDF9; border: 1px solid #EAE1D2; border-radius: 16px; overflow: hidden; cursor: pointer; transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .dealcard:hover { transform: translateY(-2px); box-shadow: 0 18px 40px -18px rgba(40,30,20,0.35); }

        .cat-pill { position: absolute; top: 14px; left: 14px; background: rgba(20,15,10,0.62); color: #fff; font-size: 12px; font-weight: 600; padding: 5px 11px; border-radius: 999px; backdrop-filter: blur(4px); }
        .wish-pill { position: absolute; top: 10px; right: 12px; background: rgba(20,15,10,0.62); border: none; color: #fff; font-size: 15px; padding: 7px 10px; border-radius: 999px; cursor: pointer; backdrop-filter: blur(4px); transition: background 0.15s; }
        .wish-pill:hover { background: rgba(20,15,10,0.8); }

        .see-details { color: #C8613A; font-weight: 600; font-size: 15px; border-bottom: 1px solid #C8613A; padding-bottom: 2px; }

        .modal { position: fixed; inset: 0; background: rgba(26,20,12,0.55); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; animation: fadeIn 0.15s ease; }
        .modal-box { background: #FFFDF9; border: 1px solid #EAE1D2; border-radius: 16px; max-width: 480px; width: 100%; max-height: 90vh; overflow-y: auto; box-shadow: 0 40px 90px -30px rgba(40,30,20,0.5); animation: slideUp 0.25s ease; }
        .modal-close { position: absolute; top: 14px; right: 14px; background: #FFFDF9; border: 1px solid #EAE1D2; border-radius: 50%; width: 32px; height: 32px; font-size: 17px; line-height: 1; cursor: pointer; color: #6B6459; }
        .modal-close:hover { color: #231F18; }

        .fd-input { width: 100%; padding: 12px 16px; border-radius: 999px; border: 1px solid #D6CDBB; background: #FFFDF9; font-size: 15px; font-family: inherit; outline: none; transition: border 0.15s, box-shadow 0.15s; color: #231F18; }
        .fd-input:focus { border-color: #C8613A; box-shadow: 0 0 0 3px rgba(200,97,58,0.15); }
        .fd-input::placeholder { color: #A79E8D; }

        .fd-error { background: #FBEDE6; color: #9C3F1E; border: 1px solid #EBC7B5; padding: 10px 14px; border-radius: 12px; font-size: 13px; font-weight: 500; }

        .hero-pill { background: #FBF7F0; border-radius: 999px; display: flex; align-items: center; padding: 6px 6px 6px 22px; gap: 10px; max-width: 460px; }
        .hero-input { border: none; background: transparent; outline: none; font-family: inherit; font-size: 15px; color: #231F18; flex: 1; min-width: 0; }
        .hero-input::placeholder { color: #6B6459; }

        .fab { position: fixed; bottom: calc(24px + env(safe-area-inset-bottom, 0px)); right: 24px; width: 58px; height: 58px; border-radius: 50%; background: #231F18; border: none; color: white; font-size: 22px; cursor: pointer; box-shadow: 0 16px 34px -12px rgba(26,20,12,0.55); z-index: 100; display: flex; align-items: center; justify-content: center; transition: transform 0.15s; }
        .fab:hover { transform: scale(1.08); }

        .plan-option { border: 1.5px solid #D6CDBB; border-radius: 14px; padding: 16px; cursor: pointer; transition: all 0.15s; background: #FFFDF9; }
        .plan-option.selected { border-color: #C8613A; background: #FBF3EC; box-shadow: 0 0 0 3px rgba(200,97,58,0.12); }
        .plan-option:hover { border-color: #C8613A; }

        .lock-note { display: inline-flex; align-items: center; gap: 5px; color: #C8613A; font-size: 12px; font-weight: 600; cursor: pointer; }
        .lock-note:hover { text-decoration: underline; }

        .search-link { display: flex; justify-content: space-between; align-items: center; padding: 13px 16px; background: #F7F1E8; border: 1px solid #EAE1D2; border-radius: 12px; text-decoration: none; color: #231F18; font-weight: 600; font-size: 14px; margin-bottom: 8px; transition: all 0.15s; }
        .search-link:hover { border-color: #C8613A; background: #FBF3EC; }

        .grid3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }
        .section-pad { padding: 44px 44px 20px; }

        @media (max-width: 1024px) {
          .grid3 { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 560px) {
          .fd-tagline { display: none !important; }
        }
        @media (max-width: 900px) {
          .fd-nav-links .fd-region-link { display: none; }
          .grid3 { grid-template-columns: 1fr; }
          .section-pad { padding: 32px 24px 12px; }
          .fd-nav-inner { padding: 14px 24px; }
          .premium-band { flex-direction: column; align-items: flex-start !important; gap: 24px; }
        }
      `}</style>

      {/* Nav */}
      <nav className="fd-nav">
        <div className="fd-nav-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <LogoMark size={42} />
            <span>
              <span className="serif" style={{ display: 'block', fontSize: 23, fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.05 }}>FareDrift</span>
              <span className="fd-tagline" style={{ display: 'block', fontSize: 8.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#B0862F', fontWeight: 600, marginTop: 2, whiteSpace: 'nowrap' }}>Est. Denver · Deals worth the drift</span>
            </span>
          </div>
          <div className="fd-nav-links">
            {regionTabs.slice(1).map(r => (
              <button key={r.id} className="fd-nav-link fd-region-link" onClick={() => filterAndScroll(r.id)}>{r.name}</button>
            ))}
            {isPremium ? (
              <button onClick={() => setShowAccount(true)} style={{ background: '#C8613A', color: '#fff', padding: '8px 16px', borderRadius: 999, fontWeight: 600, fontSize: 13, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>✦ Premium</button>
            ) : userEmail ? (
              <>
                <button className="fd-nav-link fd-signin" onClick={() => setShowAccount(true)}>Account</button>
                <button className="btn btn-ink" onClick={() => setShowUpgrade(true)}>Go Premium</button>
              </>
            ) : (
              <>
                <button className="fd-nav-link fd-signin" onClick={() => setShowLogin(true)}>Sign in</button>
                <button className="btn btn-ink" onClick={() => setShowUpgrade(true)}>Go Premium</button>
              </>
            )}
          </div>
        </div>
      </nav>

      <div style={{ maxWidth: 1440, margin: '0 auto' }}>

        {/* Banners */}
        {welcomeBanner && (
          <div style={{ margin: '16px 24px 0', background: '#EFF3E4', border: '1px solid #CBD5A8', color: '#4F5D2A', borderRadius: 12, padding: '14px 18px', fontWeight: 600, fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <span>🎉 Welcome to Premium! You now have full access to dates, airlines, and booking links.</span>
            <button onClick={() => setWelcomeBanner(false)} style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 18, cursor: 'pointer', padding: 0 }}>×</button>
          </div>
        )}
        {usingSampleData && (
          <div style={{ margin: '16px 24px 0', background: '#FBF3E0', border: '1px solid #EBD9A8', color: '#7A5E1E', borderRadius: 12, padding: '12px 18px', fontWeight: 500, fontSize: 13.5 }}>
            ⚠️ Live prices are temporarily unavailable — showing sample fares for preview. Check back shortly.
          </div>
        )}

        {/* Hero */}
        <header style={{ position: 'relative', margin: '16px 24px 0', borderRadius: 16, overflow: 'hidden', height: 'clamp(440px, 56vw, 520px)' }}>
          <HeroPhoto code={currentDealOfDay?.code} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(20,15,10,0.12) 0%, rgba(20,15,10,0.28) 45%, rgba(20,15,10,0.82) 100%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(20,15,10,0.4) 0%, rgba(20,15,10,0) 60%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 'clamp(24px, 4vw, 52px)' }}>
            <div style={{ fontSize: 13, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#F2D9C4', fontWeight: 600, marginBottom: 14, textShadow: '0 1px 10px rgba(20,15,10,0.6)' }}>
              Today's drift · Denver → {heroCity || 'anywhere'}
            </div>
            <h1 className="serif" style={{ fontWeight: 700, fontSize: 'clamp(34px, 4.8vw, 66px)', lineHeight: 0.98, color: '#FBF7F0', margin: '0 0 18px', maxWidth: 900, letterSpacing: '-0.015em', textShadow: '0 2px 18px rgba(20,15,10,0.5)' }}>
              Denver is closer to {heroPlace || 'the world'} than you think.
            </h1>
            <p style={{ fontSize: 'clamp(15px, 1.6vw, 19px)', color: '#EDE3D6', maxWidth: 560, margin: '0 0 24px', lineHeight: 1.45 }}>
              We watch every fare out of DEN around the clock and surface only the ones worth dropping everything for.
            </p>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              {isPremium || emailSignupSuccess ? (
                emailSignupSuccess ? (
                  <div style={{ background: '#FBF7F0', color: '#4F5D2A', borderRadius: 999, padding: '12px 24px', fontWeight: 600, fontSize: 15 }}>✓ You're in — first deal on its way.</div>
                ) : null
              ) : (
                <div className="hero-pill">
                  <input
                    type="email"
                    aria-label="Email address for free deal alerts"
                    className="hero-input"
                    placeholder="you@denver.com"
                    value={emailSignup}
                    onChange={(e) => setEmailSignup(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleEmailSignup()}
                  />
                  <button className="btn btn-terra" onClick={handleEmailSignup}>Get the deals</button>
                </div>
              )}
              {currentDealOfDay && (
                <button
                  onClick={() => { setSelected(currentDealOfDay); setShowModal(true); }}
                  style={{ background: 'none', border: 'none', color: '#F2D9C4', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid rgba(242,217,196,0.6)', paddingBottom: 2 }}>
                  {fmtPrice(currentDealOfDay.price)} round trip — see the deal →
                </button>
              )}
            </div>
            {emailSignupError && <div className="fd-error" style={{ marginTop: 12, maxWidth: 460 }}>{emailSignupError}</div>}
          </div>
        </header>

        {/* Deal feed */}
        <section id="deals">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '40px 44px 8px', flexWrap: 'wrap', gap: 14 }}>
            <h2 className="serif" style={{ fontSize: 30, fontWeight: 700, margin: 0 }}>Worth the detour</h2>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {regionTabs.map(r => (
                <button key={r.id} className={`chip ${region === r.id ? 'on' : ''}`} onClick={() => setRegion(r.id)}>{r.name}</button>
              ))}
            </div>
          </div>

          {deals.length === 0 ? (
            <div style={{ margin: '22px 44px 44px', background: '#FFFDF9', border: '1px dashed #D6CDBB', borderRadius: 16, padding: 48, textAlign: 'center', color: '#6B6459' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🛫</div>
              <p className="serif" style={{ margin: 0, fontWeight: 700, fontSize: 20, color: '#231F18' }}>No live deals in this region right now.</p>
              <p style={{ margin: '8px 0 0', fontSize: 15 }}>New fares land daily — check back soon, or sign up above and we'll email you the moment one drops.</p>
            </div>
          ) : (
            <div className="grid3" style={{ padding: '22px 44px 24px' }}>
              {deals.map(deal => {
                const regionName = deal.region || deal.destination?.region;
                return (
                  <div key={deal.id} className="dealcard" onClick={() => { setSelected(deal); setShowModal(true); }}>
                    <div style={{ position: 'relative', height: 210 }}>
                      <Photo
                        src={`${IMG_BASE}/images/deals/${deal.code}.jpg`}
                        alt=""
                        fallback={
                          <div style={{ position: 'absolute', inset: 0, background: REGION_ART[regionName] || REGION_ART['Europe'], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: 74, filter: 'drop-shadow(0 6px 16px rgba(20,15,10,0.35))' }}>{deal.emoji || deal.destination?.emoji}</span>
                          </div>
                        }
                      />
                      <span className="cat-pill">{regionName}</span>
                      <button
                        className="wish-pill"
                        aria-label={inWish(deal) ? 'Remove from dream list' : 'Save to dream list'}
                        onClick={(e) => { e.stopPropagation(); toggleWish(deal); }}>
                        {inWish(deal) ? '❤️' : '🤍'}
                      </button>
                    </div>
                    <div style={{ padding: '20px 22px 22px' }}>
                      <div className="serif" style={{ fontSize: 25, fontWeight: 700 }}>{deal.city || deal.destination?.city} {deal.emoji || deal.destination?.emoji}</div>
                      <div style={{ fontSize: 14, color: '#8A8378', margin: '2px 0 12px' }}>{deal.country || deal.destination?.country} · from Denver (DEN)</div>
                      <p style={{ fontSize: 15, color: '#4A4437', lineHeight: 1.45, margin: '0 0 16px', fontStyle: 'italic' }}>{sentence(deal.dream || deal.destination?.dream)}</p>
                      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                        <div>
                          {deal.wasPrice > 0 && <div style={{ fontSize: 12, color: '#A79E8D', textDecoration: 'line-through' }}>{fmtPrice(deal.wasPrice)}</div>}
                          <div className="serif" style={{ fontSize: 30, fontWeight: 800, color: '#231F18' }}>{fmtPrice(deal.price)}</div>
                        </div>
                        <span className="see-details">See details →</span>
                      </div>
                      <div style={{ fontSize: 13, color: '#8A8378', marginTop: 12, borderTop: '1px solid #F0E8D8', paddingTop: 12 }}>
                        {isPremium ? (
                          <>{deal.departDate ? `${fmt(deal.departDate)} – ${fmt(deal.returnDate)}` : 'Dates coming soon'} · {stopsText(deal.stops)}{deal.airline ? ` · ${deal.airline}` : ''}</>
                        ) : (
                          <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span>{deal.departDate ? fmtMonth(deal.departDate) : 'Dates coming soon'}</span>
                            <span className="lock-note" onClick={(e) => { e.stopPropagation(); openUpgrade(); }}>🔒 Dates &amp; airline with Premium</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Surprise me */}
          <div style={{ textAlign: 'center', padding: '0 44px 20px' }}>
            <button className="btn btn-outline" onClick={spin} disabled={spinning || !deals.length}>
              {spinning ? '🎲 Spinning…' : "🎲 Can't decide? Let fate pick"}
            </button>
            {adventure && !spinning && (
              <div style={{ maxWidth: 460, margin: '16px auto 0', background: '#FFFDF9', border: '1px solid #EAE1D2', borderRadius: 16, padding: '16px 22px', display: 'flex', alignItems: 'center', gap: 14, animation: 'slideUp 0.3s ease' }}>
                <span style={{ fontSize: 30 }}>{adventure.emoji || adventure.destination?.emoji}</span>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div className="serif" style={{ fontWeight: 700, fontSize: 18 }}>Fate says: {adventure.city || adventure.destination?.city}</div>
                  <div style={{ color: '#8A8378', fontSize: 13 }}>{fmtPrice(adventure.price)} round trip</div>
                </div>
                <button className="btn btn-terra" style={{ padding: '9px 18px', fontSize: 14 }} onClick={() => { setSelected(adventure); setShowModal(true); }}>Explore →</button>
              </div>
            )}
          </div>
        </section>

        {/* How the drift works */}
        <section className="section-pad">
          <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 40px' }}>
            <div className="eyebrow" style={{ marginBottom: 12 }}>How the drift works</div>
            <h2 className="serif" style={{ fontSize: 'clamp(28px, 3vw, 36px)', fontWeight: 700, margin: 0, lineHeight: 1.05 }}>Three steps between you and the fjords.</h2>
          </div>
          <div className="grid3" style={{ gap: 40 }}>
            {[
              { n: '01', t: "Tell us you're in Denver", b: 'Drop your email — free, no spam. We start watching every fare out of DEN the moment you sign up.' },
              { n: '02', t: 'We surface the drops', b: 'When a price falls off a cliff, it lands in your inbox — hand-checked, round-trip, and worth dropping everything for.' },
              { n: '03', t: 'Book and save hundreds', b: 'Follow the link, book direct with the airline, and go. Members save hundreds on international round-trips.' },
            ].map(step => (
              <div key={step.n}>
                <div className="serif" style={{ fontSize: 64, fontWeight: 800, color: '#E7D3C2', lineHeight: 0.9, marginBottom: 14 }}>{step.n}</div>
                <div className="serif" style={{ fontSize: 23, fontWeight: 700, marginBottom: 8 }}>{step.t}</div>
                <p style={{ fontSize: 15, color: '#6B6459', lineHeight: 1.55, margin: 0 }}>{step.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why FareDrift */}
        <section className="section-pad" style={{ paddingTop: 48 }}>
          <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 36px' }}>
            <div className="eyebrow" style={{ marginBottom: 12 }}>Why FareDrift</div>
            <h2 className="serif" style={{ fontSize: 'clamp(28px, 3vw, 36px)', fontWeight: 700, margin: 0, lineHeight: 1.05 }}>Built in Denver. Watching DEN like a hawk.</h2>
          </div>
          <div className="grid3">
            {[
              { label: 'Coverage', q: '34 dream destinations across Europe, Asia, the Pacific, and the Caribbean — every fare out of DEN, checked daily.' },
              { label: 'Honesty', q: 'Every price is a live round-trip fare, weighed against weeks of price history. When we say 16% off, it actually is.' },
              { label: 'Curation', q: 'Ordinary fares never make the page. A deal earns its place only when it’s worth dropping everything for.' },
            ].map(t => (
              <div key={t.label} style={{ background: '#FFFDF9', border: '1px solid #EAE1D2', borderRadius: 16, padding: '28px 30px' }}>
                <div style={{ color: '#B0862F', fontSize: 12, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 14 }}>{t.label}</div>
                <p className="serif" style={{ fontSize: 20, fontStyle: 'italic', lineHeight: 1.4, color: '#231F18', margin: 0 }}>{t.q}</p>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', margin: '28px auto 0', maxWidth: 520, fontSize: 14.5, color: '#6B6459' }}>
            Booked a FareDrift deal? <span className="serif" style={{ fontStyle: 'italic' }}>Be our first story</span> — just reply to any deal email and tell us where you went.
          </p>
        </section>

        {/* Premium band */}
        <section className="premium-band" style={{ margin: '44px 24px 24px', background: '#231F18', borderRadius: 16, padding: 'clamp(28px, 4vw, 48px) clamp(24px, 4vw, 52px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#F7F1E8', gap: 24 }}>
          <div style={{ maxWidth: 640 }}>
            <div className="eyebrow" style={{ color: '#E0A585', marginBottom: 12 }}>FareDrift Premium</div>
            <h2 className="serif" style={{ fontSize: 'clamp(28px, 3.2vw, 38px)', fontWeight: 700, margin: '0 0 12px', lineHeight: 1.05 }}>Every deal, the moment it drops.</h2>
            <p style={{ fontSize: 17, color: '#CFC6B8', margin: 0, lineHeight: 1.5 }}>
              Exact dates, airlines, one-click booking links, and daily alerts before deals sell out. {IS_NATIVE_APP ? `${rcPrices?.monthly || '$4.99'}/month or ${rcPrices?.yearly || '$49'}/year.` : '$4.99/month or $49/year.'}
            </p>
          </div>
          {isPremium ? (
            <span style={{ background: '#C8613A', color: '#fff', padding: '14px 28px', borderRadius: 999, fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' }}>✦ You're Premium</span>
          ) : (
            <button className="btn btn-terra" style={{ padding: '16px 32px', fontWeight: 700, fontSize: 16 }} onClick={() => setShowUpgrade(true)}>Start Premium</button>
          )}
        </section>

        {/* Wishlist summary */}
        {wishlist.length > 0 && (
          <div style={{ margin: '0 24px 24px', background: '#FFFDF9', border: '1px solid #EAE1D2', borderRadius: 16, padding: '20px 26px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
            <div>
              <div className="serif" style={{ fontSize: 19, fontWeight: 700 }}>❤️ Your dream list</div>
              <p style={{ margin: '3px 0 0', color: '#8A8378', fontSize: 14 }}>{wishlist.length} adventure{wishlist.length > 1 ? 's' : ''} saved</p>
            </div>
            <button className="btn btn-ink" onClick={() => setShowWishlist(true)}>View my dreams →</button>
          </div>
        )}

        {/* Footer */}
        <footer style={{ padding: '28px 44px 36px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #E6DDCD', fontSize: 14, color: '#8A8378', flexWrap: 'wrap', gap: 10 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <LogoMark size={26} />
            <span className="serif" style={{ fontSize: 18, fontWeight: 700, color: '#231F18' }}>FareDrift</span>
          </span>
          <span style={{ color: '#B0862F', fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600 }}>Est. Denver · Deals worth the drift</span>
        </footer>
      </div>

      {/* FAB */}
      {wishlist.length > 0 && (
        <button className="fab" onClick={() => setShowWishlist(true)} aria-label="Open dream list">
          ❤️
          <span style={{ position: 'absolute', top: -4, right: -4, background: '#C8613A', color: 'white', width: 22, height: 22, borderRadius: '50%', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{wishlist.length}</span>
        </button>
      )}

      {/* Detail Modal */}
      {showModal && selected && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ background: '#F7F1E8', padding: '32px 28px 26px', textAlign: 'center', position: 'relative', borderBottom: '1px solid #EAE1D2' }}>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
              <span style={{ display: 'inline-block', background: '#FFFDF9', border: '1px solid #EAE1D2', padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, color: '#6B6459' }}>{selected.region || selected.destination?.region}</span>
              <span style={{ fontSize: 54, display: 'block', margin: '14px 0 8px' }}>{selected.emoji || selected.destination?.emoji}</span>
              <h2 className="serif" style={{ margin: '0 0 2px', fontSize: 30, fontWeight: 800, letterSpacing: '-0.01em' }}>{selected.city || selected.destination?.city}</h2>
              <p style={{ margin: 0, color: '#8A8378', fontSize: 14 }}>{selected.country || selected.destination?.country} · from Denver (DEN)</p>
              <div className="serif" style={{ marginTop: 16, fontSize: 38, fontWeight: 800 }}>{fmtPrice(selected.price)}</div>
              <p style={{ margin: '2px 0 0', color: '#8A8378', fontSize: 13 }}>round trip</p>
            </div>
            <div style={{ padding: '18px 28px', borderBottom: '1px solid #F0E8D8' }}>
              <p className="serif" style={{ margin: 0, fontSize: 17, fontStyle: 'italic', color: '#4A4437', textAlign: 'center', lineHeight: 1.45 }}>
                {sentence(selected.dream || selected.destination?.dream)}
              </p>
            </div>
            <div style={{ padding: '22px 28px 28px' }}>
              <h4 className="serif" style={{ margin: '0 0 12px', fontWeight: 700, fontSize: 18 }}>Trip details</h4>
              <div style={{ background: '#F7F1E8', border: '1px solid #F0E8D8', borderRadius: 12, padding: 18, marginBottom: 20, fontSize: 14 }}>
                {isPremium ? (
                  <>
                    {[
                      ['Depart', selected.departDate ? fmtFull(selected.departDate) : 'Coming soon'],
                      ['Return', selected.returnDate ? fmtFull(selected.returnDate) : 'Coming soon'],
                      ['Duration', selected.duration ? `${selected.duration} days` : '—'],
                      ['Airline', selected.airline || '—'],
                      ['Route', stopsText(selected.stops)],
                    ].map(([label, value], i, arr) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: i < arr.length - 1 ? 10 : 0 }}>
                        <span style={{ color: '#8A8378', fontWeight: 500 }}>{label}</span>
                        <span style={{ fontWeight: 600 }}>{value}</span>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#8A8378', fontWeight: 500 }}>Travel month</span>
                      <span style={{ fontWeight: 600 }}>{selected.departDate ? fmtMonth(selected.departDate) : 'Coming soon'}</span>
                    </div>
                    <div style={{ borderTop: '1px dashed #D6CDBB', marginTop: 12, paddingTop: 14 }}>
                      <div onClick={() => { setShowModal(false); openUpgrade(); }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: 10 }}>
                        <div>
                          <span style={{ color: '#C8613A', fontWeight: 600, fontSize: 13 }}>🔒 Unlock full details</span>
                          <p style={{ margin: '3px 0 0', color: '#A79E8D', fontSize: 12 }}>Exact dates, duration, airline &amp; stops</p>
                        </div>
                        <span className="btn btn-terra" style={{ padding: '8px 18px', fontSize: 13 }}>Upgrade</span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <h4 className="serif" style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 18 }}>Search this flight</h4>
              {isPremium ? (
                ['Google Flights', 'Skyscanner', 'Kayak'].map(s => {
                  const code = selected.code || selected.destination?.code || '';
                  const searchUrl = s === 'Google Flights'
                    ? `https://www.google.com/travel/flights?q=DEN+to+${code}+${selected.departDate ? new Date(selected.departDate).toISOString().split('T')[0] : ''}`
                    : s === 'Skyscanner'
                    ? `https://www.skyscanner.com/transport/flights/den/${code.toLowerCase()}/`
                    : `https://www.kayak.com/flights/DEN-${code}/`;
                  return (
                    <a key={s} href={searchUrl} target="_blank" rel="noopener noreferrer" className="search-link">
                      <span>{s}</span><span style={{ color: '#C8613A' }}>Search →</span>
                    </a>
                  );
                })
              ) : (
                <div style={{ position: 'relative' }}>
                  {['Google Flights', 'Skyscanner', 'Kayak'].map(s => (
                    <div key={s} className="search-link" style={{ color: '#C6BCA8', filter: 'blur(2px)', userSelect: 'none' }}><span>{s}</span><span>Search →</span></div>
                  ))}
                  <div onClick={() => { setShowModal(false); openUpgrade(); }} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,253,249,0.6)', borderRadius: 12, cursor: 'pointer' }}>
                    <span className="btn btn-terra" style={{ padding: '10px 20px', fontSize: 13.5 }}>🔒 Unlock booking links</span>
                  </div>
                </div>
              )}

              <button
                onClick={() => toggleWish(selected)}
                className="btn"
                style={{ width: '100%', marginTop: 18, padding: 14, background: inWish(selected) ? '#FBF3EC' : '#231F18', border: inWish(selected) ? '1px solid #EBC7B5' : 'none', borderRadius: 999, color: inWish(selected) ? '#C8613A' : '#F7F1E8', fontSize: 15, fontWeight: 700 }}>
                {inWish(selected) ? '❤️ Saved to my dreams' : '🤍 Add to my dreams'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wishlist Modal */}
      {showWishlist && (
        <div className="modal" onClick={() => setShowWishlist(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ padding: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 className="serif" style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>❤️ My dreams</h2>
                <button className="modal-close" style={{ position: 'static' }} onClick={() => setShowWishlist(false)}>×</button>
              </div>
              {wishlist.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 36, color: '#8A8378' }}><span style={{ fontSize: 40 }}>💭</span><p>Your dream list is empty.</p></div>
              ) : (
                wishlist.map(d => (
                  <div key={d.id} style={{ background: '#F7F1E8', border: '1px solid #F0E8D8', borderRadius: 12, padding: 14, display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
                    <span style={{ fontSize: 28 }}>{d.emoji || d.destination?.emoji}</span>
                    <div style={{ flex: 1 }}>
                      <div className="serif" style={{ fontWeight: 700, fontSize: 17 }}>{d.city || d.destination?.city}</div>
                      <p style={{ margin: '2px 0 0', color: '#A79E8D', fontSize: 12 }}>{d.region || d.destination?.region}{d.departDate ? ` · ${fmt(d.departDate)} – ${fmt(d.returnDate)}` : ''}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="serif" style={{ fontWeight: 800, fontSize: 18 }}>{fmtPrice(d.price)}</div>
                      <button onClick={() => toggleWish(d)} style={{ background: 'none', border: 'none', color: '#C8613A', fontSize: 12, cursor: 'pointer', fontWeight: 600, padding: 0, fontFamily: 'inherit' }}>Remove</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgrade && (
        <div className="modal" onClick={() => setShowUpgrade(false)}>
          <div className="modal-box" style={{ maxWidth: 430 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '34px 32px', position: 'relative' }}>
              <button className="modal-close" onClick={() => setShowUpgrade(false)}>×</button>
              <div style={{ textAlign: 'center', marginBottom: 22 }}>
                <div className="eyebrow" style={{ marginBottom: 10 }}>FareDrift Premium</div>
                <h2 className="serif" style={{ margin: '0 0 6px', fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>Every deal, the moment it drops.</h2>
                <p style={{ margin: 0, color: '#8A8378', fontSize: 14 }}>Everything you need to actually book the deal</p>
              </div>

              <div style={{ background: '#F7F1E8', border: '1px solid #F0E8D8', borderRadius: 14, padding: '18px 20px', marginBottom: 20, textAlign: 'left' }}>
                {[
                  { feature: 'Exact travel dates', desc: 'Departure & return dates for every deal' },
                  { feature: 'Airline & stops', desc: 'Know who you\'re flying and how direct it is' },
                  { feature: 'Direct booking links', desc: 'One click to Google Flights, Skyscanner & Kayak' },
                  { feature: 'Daily deal emails', desc: 'The best fares in your inbox every morning' },
                ].map((f, i, arr) => (
                  <div key={f.feature} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: i < arr.length - 1 ? 12 : 0 }}>
                    <span style={{ color: '#C8613A', fontSize: 15, marginTop: 1, fontWeight: 700 }}>✓</span>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{f.feature}</span>
                      <p style={{ margin: '1px 0 0', fontSize: 12.5, color: '#A79E8D' }}>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
                <div className={`plan-option ${upgradePlan === 'monthly' ? 'selected' : ''}`} onClick={() => setUpgradePlan('monthly')} style={{ flex: 1, textAlign: 'center' }}>
                  <div className="serif" style={{ fontWeight: 800, fontSize: 21 }}>{IS_NATIVE_APP ? (rcPrices?.monthly || '$4.99') : '$4.99'}</div>
                  <div style={{ fontSize: 12, color: '#A79E8D', fontWeight: 500 }}>per month</div>
                </div>
                <div className={`plan-option ${upgradePlan === 'yearly' ? 'selected' : ''}`} onClick={() => setUpgradePlan('yearly')} style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: -9, right: 10, background: '#C8613A', color: 'white', fontSize: 10, padding: '2px 8px', borderRadius: 999, fontWeight: 700 }}>SAVE 18%</div>
                  <div className="serif" style={{ fontWeight: 800, fontSize: 21 }}>{IS_NATIVE_APP ? (rcPrices?.yearly || '$49') : '$49'}</div>
                  <div style={{ fontSize: 12, color: '#A79E8D', fontWeight: 500 }}>per year</div>
                </div>
              </div>

              {!IS_NATIVE_APP && (
                <input
                  type="email"
                  placeholder="you@denver.com"
                  value={upgradeEmail}
                  onChange={(e) => setUpgradeEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUpgrade()}
                  className="fd-input"
                  style={{ marginBottom: 12 }}
                />
              )}
              {upgradeError && <div className="fd-error" style={{ marginBottom: 12 }}>{upgradeError}</div>}

              <button className="btn btn-terra" onClick={handleUpgrade} disabled={upgradeLoading} style={{ width: '100%', padding: 14, fontSize: 15, fontWeight: 700 }}>
                {upgradeLoading
                  ? (IS_NATIVE_APP ? 'Confirming with the App Store…' : 'Redirecting to checkout…')
                  : `Start Premium — ${upgradePlan === 'yearly' ? `${(IS_NATIVE_APP && rcPrices?.yearly) || '$49'}/year` : `${(IS_NATIVE_APP && rcPrices?.monthly) || '$4.99'}/month`}`}
              </button>

              {IS_NATIVE_APP ? (
                <button onClick={handleRestore} disabled={restoreLoading} style={{ marginTop: 12, width: '100%', background: 'none', border: 'none', color: '#C8613A', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600 }}>
                  {restoreLoading ? 'Restoring…' : 'Restore Purchases'}
                </button>
              ) : (
                <button onClick={() => setShowUpgrade(false)} style={{ marginTop: 12, width: '100%', background: 'none', border: 'none', color: '#A79E8D', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5 }}>Maybe later</button>
              )}

              <p style={{ margin: '14px 0 0', fontSize: 11.5, color: '#A79E8D', textAlign: 'center' }}>
                {IS_NATIVE_APP
                  ? '🔒 Secure payment via the App Store · Cancel anytime in Settings'
                  : '🔒 Secure payment powered by Stripe · Cancel anytime'}
              </p>
              {IS_NATIVE_APP && (
                <p style={{ margin: '10px 0 0', fontSize: 10.5, color: '#A79E8D', textAlign: 'center', lineHeight: 1.5 }}>
                  Payment is charged to your Apple Account at confirmation of purchase. The subscription automatically renews unless cancelled at least 24 hours before the end of the current period. Manage or cancel anytime in your Apple Account settings.
                  {' '}
                  <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noopener noreferrer" style={{ color: '#C8613A' }}>Terms of Use</a>
                  {' · '}
                  <a href="https://faredrift.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#C8613A' }}>Privacy Policy</a>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Account Modal — sign out & account deletion (App Store Guideline 5.1.1(v)) */}
      {showAccount && userEmail && (
        <div className="modal" onClick={() => { setShowAccount(false); setDeleteConfirm(false); setDeleteError(''); }}>
          <div className="modal-box" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '34px 32px', position: 'relative' }}>
              <button className="modal-close" onClick={() => { setShowAccount(false); setDeleteConfirm(false); setDeleteError(''); }}>×</button>

              {!deleteConfirm ? (
                <>
                  <h2 className="serif" style={{ margin: '0 0 6px', textAlign: 'center', fontSize: 26, fontWeight: 800 }}>Your account</h2>
                  <p style={{ textAlign: 'center', color: '#8A8378', marginBottom: 6, fontSize: 14 }}>Signed in as</p>
                  <p style={{ textAlign: 'center', fontWeight: 600, marginTop: 0, marginBottom: 22, fontSize: 15, wordBreak: 'break-all' }}>{userEmail}</p>
                  {isPremium && (
                    <div style={{ background: '#F7F1E8', border: '1px solid #F0E8D8', borderRadius: 12, padding: '12px 16px', marginBottom: 20, fontSize: 13.5, color: '#4A4437', textAlign: 'center' }}>
                      ✦ Premium member
                    </div>
                  )}
                  <button className="btn btn-ink" onClick={handleSignOut} style={{ width: '100%', padding: 13, fontSize: 15, marginBottom: 12 }}>Sign out</button>
                  <button onClick={() => setDeleteConfirm(true)} style={{ width: '100%', background: 'none', border: '1px solid #D8CFBE', borderRadius: 999, padding: 12, color: '#A0442A', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600 }}>
                    Delete my account
                  </button>
                </>
              ) : (
                <>
                  <h2 className="serif" style={{ margin: '0 0 10px', textAlign: 'center', fontSize: 24, fontWeight: 800 }}>Delete your account?</h2>
                  <p style={{ color: '#4A4437', fontSize: 14, lineHeight: 1.55, marginBottom: 12 }}>
                    This permanently deletes your FareDrift account and all data associated with it — your email address, saved destinations, and email preferences. This cannot be undone.
                  </p>
                  {IS_NATIVE_APP && isPremium && (
                    <p style={{ color: '#8A8378', fontSize: 12.5, lineHeight: 1.5, marginBottom: 12 }}>
                      Note: if you have an active subscription billed through Apple, deleting your account does not cancel it. Manage it in Settings → your name → Subscriptions.
                    </p>
                  )}
                  {deleteError && <div className="fd-error" style={{ marginBottom: 12 }}>{deleteError}</div>}
                  <button onClick={handleDeleteAccount} disabled={deleteLoading} style={{ width: '100%', background: '#A0442A', border: 'none', borderRadius: 999, padding: 13, color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, marginBottom: 12, opacity: deleteLoading ? 0.7 : 1 }}>
                    {deleteLoading ? 'Deleting…' : 'Yes, permanently delete my account'}
                  </button>
                  <button onClick={() => { setDeleteConfirm(false); setDeleteError(''); }} style={{ width: '100%', background: 'none', border: 'none', color: '#A79E8D', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5 }}>Keep my account</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Deletion complete confirmation */}
      {deleteDone && (
        <div className="modal" onClick={() => { setDeleteDone(false); setShowAccount(false); }}>
          <div className="modal-box" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '34px 32px', textAlign: 'center' }}>
              <h2 className="serif" style={{ margin: '0 0 10px', fontSize: 24, fontWeight: 800 }}>Account deleted</h2>
              <p style={{ color: '#4A4437', fontSize: 14, lineHeight: 1.55, marginBottom: 18 }}>
                Your account and all associated data have been permanently removed. Safe travels. ✈️
              </p>
              <button className="btn btn-ink" onClick={() => { setDeleteDone(false); setShowAccount(false); }} style={{ padding: '12px 28px', fontSize: 15 }}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLogin && (
        <div className="modal" onClick={() => setShowLogin(false)}>
          <div className="modal-box" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '34px 32px', position: 'relative' }}>
              <button className="modal-close" onClick={() => setShowLogin(false)}>×</button>
              <h2 className="serif" style={{ margin: '0 0 6px', textAlign: 'center', fontSize: 26, fontWeight: 800 }}>Welcome back</h2>
              <p style={{ textAlign: 'center', color: '#8A8378', marginBottom: 22, fontSize: 14 }}>Enter the email you subscribed with to restore Premium access.</p>

              {loginError && <div className="fd-error" style={{ marginBottom: 14 }}>{loginError}</div>}

              <input
                type="email"
                placeholder="you@denver.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                className="fd-input"
                style={{ marginBottom: 14 }}
              />

              <button className="btn btn-ink" onClick={handleLogin} disabled={loginLoading} style={{ width: '100%', padding: 13, fontSize: 15 }}>
                {loginLoading ? 'Checking…' : 'Sign in'}
              </button>

              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <button onClick={() => { setShowLogin(false); setShowUpgrade(true); }} style={{ background: 'none', border: 'none', color: '#C8613A', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: 13.5 }}>
                  Don't have Premium? Upgrade now →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
